import {
  mockData,
  mockCategories,
  mockCustomers,
  mockOrders,
  mockDashboardData,
  mockReportsData,
} from './mockData';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// Helper function to get current tenant slug
const getCurrentTenantSlug = () => {
  // For now, return the actual tenant slug from backend - this should be replaced with actual tenant resolution
  return 'acme';
};

// Using mock data directly without network requests

// Mock response generator that simulates API responses
const createMockResponse = (data, delay = 300) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ 
        data, 
        status: 200, 
        statusText: 'OK (Mock Data)',
        headers: {},
        config: {}
      });
    }, delay);
  });
};

// E-Way Bills API - Real backend implementation
export const ewayBillsAPI = {
  getAll: async (params = {}) => {
    // Loading e-way bills from backend
    const tenantSlug = getCurrentTenantSlug();
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/ewaybills${queryParams ? `?${queryParams}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch e-way bills: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data, pagination: result.pagination };
  },

  getById: async (id) => {
    console.log('🧾 Loading e-way bill by ID:', id);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch e-way bill: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },

  create: async (bill) => {
    console.log('🧾 Creating e-way bill:', bill);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
      body: JSON.stringify(bill),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create e-way bill: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },

  update: async (id, updates) => {
    console.log('🧾 Updating e-way bill:', id, updates);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update e-way bill: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },

  delete: async (id) => {
    console.log('🧾 Deleting e-way bill:', id);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to delete e-way bill: ${response.statusText}`);
    }
    
    return { success: true };
  },

  generate: async (id) => {
    console.log('🧾 Generating e-way bill number:', id);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills/${id}/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to generate e-way bill: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },

  cancel: async (id, reason) => {
    console.log('🧾 Cancelling e-way bill:', id, reason);
    const tenantSlug = getCurrentTenantSlug();
    
    const response = await fetch(`${API_BASE_URL}/ewaybills/${id}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to cancel e-way bill: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },

  getStats: async (params = {}) => {
    console.log('🧾 Loading e-way bills statistics');
    const tenantSlug = getCurrentTenantSlug();
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/ewaybills/stats/summary${queryParams ? `?${queryParams}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': tenantSlug,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch e-way bills statistics: ${response.statusText}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
};

// Helper function to simulate API errors for testing
const createMockError = (message = 'Mock error', code = 'ERR_MOCK') => {
  return Promise.reject({
    code,
    message,
    response: null
  });
};

// Helper function to find item by ID in array
const findById = (array, id) => {
  return array.find(item => item.id === parseInt(id));
};

// Helper function to generate new ID
const generateId = (array) => {
  return Math.max(...array.map(item => item.id), 0) + 1;
};

// Auth API - Mock implementation
export const authAPI = {
  login: (credentials) => {
    console.log('🔐 Mock login:', credentials);
    return createMockResponse({ 
      token: 'mock-jwt-token', 
      user: { id: 1, name: 'Admin User', email: credentials.email }
    });
  },
  logout: () => createMockResponse({ success: true }),
  register: (userData) => createMockResponse({ 
    user: { ...userData, id: generateId([]) }, 
    token: 'mock-jwt-token' 
  }),
  refreshToken: () => createMockResponse({ token: 'mock-refreshed-token' }),
  forgotPassword: (email) => createMockResponse({ message: 'Password reset email sent' }),
  resetPassword: (token, password) => createMockResponse({ message: 'Password reset successful' }),
};

// Products API - Mock implementation
export const productsAPI = {
  getAll: () => {
    // console.log('📦 Loading products from mock data');
    return createMockResponse(mockData.products);
  },
  getById: (id) => {
    const product = findById(mockData.products, id);
    return product ? createMockResponse(product) : createMockError('Product not found', 'ERR_NOT_FOUND');
  },
  create: (product) => {
    const newProduct = { ...product, id: generateId(mockData.products), createdAt: new Date().toISOString() };
    mockData.products.push(newProduct);
    return createMockResponse(newProduct);
  },
  update: (id, product) => {
    const index = mockData.products.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      mockData.products[index] = { ...mockData.products[index], ...product, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.products[index]);
    }
    return createMockError('Product not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    const index = mockData.products.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      mockData.products.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Product not found', 'ERR_NOT_FOUND');
  },
  search: (query) => {
    const results = mockData.products.filter(p => 
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.sku.toLowerCase().includes(query.toLowerCase()) ||
      p.description?.toLowerCase().includes(query.toLowerCase())
    );
    return createMockResponse(results);
  },
  getByCategory: (categoryId) => {
    const results = mockData.products.filter(p => p.categoryId === parseInt(categoryId));
    return createMockResponse(results);
  },
  updateStock: (id, quantity) => {
    const product = findById(mockData.products, id);
    if (product) {
      product.stock = quantity;
      return createMockResponse(product);
    }
    return createMockError('Product not found', 'ERR_NOT_FOUND');
  },
};

// Categories API - Backend implementation
export const categoriesAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
  
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
  
  create: async (category) => {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      body: JSON.stringify(category),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
  
  update: async (id, category) => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      body: JSON.stringify(category),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
  
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
  
  getHierarchy: async () => {
    const response = await fetch(`${API_BASE_URL}/categories/hierarchy/tree`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getCurrentTenantSlug(),
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.data };
  },
};

// Locations API - Mock implementation
export const locationsAPI = {
  getAll: () => {
    // console.log('📍 Loading locations from mock data');
    return createMockResponse(mockData.locations || []);
  },
  getById: (id) => {
    const location = findById(mockData.locations || [], id);
    return location ? createMockResponse(location) : createMockError('Location not found', 'ERR_NOT_FOUND');
  },
  create: (location) => {
    if (!mockData.locations) mockData.locations = [];
    const newLocation = { ...location, id: generateId(mockData.locations), createdAt: new Date().toISOString() };
    mockData.locations.push(newLocation);
    return createMockResponse(newLocation);
  },
  update: (id, location) => {
    if (!mockData.locations) return createMockError('Location not found', 'ERR_NOT_FOUND');
    const index = mockData.locations.findIndex(l => l.id === parseInt(id));
    if (index !== -1) {
      mockData.locations[index] = { ...mockData.locations[index], ...location, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.locations[index]);
    }
    return createMockError('Location not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    if (!mockData.locations) return createMockError('Location not found', 'ERR_NOT_FOUND');
    const index = mockData.locations.findIndex(l => l.id === parseInt(id));
    if (index !== -1) {
      mockData.locations.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Location not found', 'ERR_NOT_FOUND');
  },
};

// Vendors API - Mock implementation
export const vendorsAPI = {
  getAll: () => {
    // console.log('🏢 Loading vendors from mock data');
    return createMockResponse(mockData.vendors || []);
  },
  getById: (id) => {
    const vendor = findById(mockData.vendors || [], id);
    return vendor ? createMockResponse(vendor) : createMockError('Vendor not found', 'ERR_NOT_FOUND');
  },
  create: (vendor) => {
    if (!mockData.vendors) mockData.vendors = [];
    const newVendor = { ...vendor, id: generateId(mockData.vendors), createdAt: new Date().toISOString() };
    mockData.vendors.push(newVendor);
    return createMockResponse(newVendor);
  },
  update: (id, vendor) => {
    if (!mockData.vendors) return createMockError('Vendor not found', 'ERR_NOT_FOUND');
    const index = mockData.vendors.findIndex(v => v.id === parseInt(id));
    if (index !== -1) {
      mockData.vendors[index] = { ...mockData.vendors[index], ...vendor, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.vendors[index]);
    }
    return createMockError('Vendor not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    if (!mockData.vendors) return createMockError('Vendor not found', 'ERR_NOT_FOUND');
    const index = mockData.vendors.findIndex(v => v.id === parseInt(id));
    if (index !== -1) {
      mockData.vendors.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Vendor not found', 'ERR_NOT_FOUND');
  },
};

// Customers API - Mock implementation
export const customersAPI = {
  getAll: () => {
    // console.log('👥 Loading customers from mock data');
    return createMockResponse(mockCustomers);
  },
  getById: (id) => {
    const customer = findById(mockCustomers, id);
    return customer ? createMockResponse(customer) : createMockError('Customer not found', 'ERR_NOT_FOUND');
  },
  create: (customer) => {
    const newCustomer = { ...customer, id: generateId(mockCustomers), createdAt: new Date().toISOString() };
    mockCustomers.push(newCustomer);
    return createMockResponse(newCustomer);
  },
  update: (id, customer) => {
    const index = mockCustomers.findIndex(c => c.id === parseInt(id));
    if (index !== -1) {
      mockCustomers[index] = { ...mockCustomers[index], ...customer, updatedAt: new Date().toISOString() };
      return createMockResponse(mockCustomers[index]);
    }
    return createMockError('Customer not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    const index = mockCustomers.findIndex(c => c.id === parseInt(id));
    if (index !== -1) {
      mockCustomers.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Customer not found', 'ERR_NOT_FOUND');
  },
  getOrders: (id) => {
    const customerOrders = mockOrders.filter(o => o.customerId === parseInt(id));
    return createMockResponse(customerOrders);
  },
};

// Orders API - Mock implementation
export const ordersAPI = {
  getAll: () => {
    // console.log('📋 Loading orders from mock data');
    return createMockResponse(mockOrders);
  },
  getById: (id) => {
    const order = findById(mockOrders, id);
    return order ? createMockResponse(order) : createMockError('Order not found', 'ERR_NOT_FOUND');
  },
  create: (order) => {
    const newOrder = { ...order, id: generateId(mockOrders), createdAt: new Date().toISOString() };
    mockOrders.push(newOrder);
    return createMockResponse(newOrder);
  },
  update: (id, order) => {
    const index = mockOrders.findIndex(o => o.id === parseInt(id));
    if (index !== -1) {
      mockOrders[index] = { ...mockOrders[index], ...order, updatedAt: new Date().toISOString() };
      return createMockResponse(mockOrders[index]);
    }
    return createMockError('Order not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    const index = mockOrders.findIndex(o => o.id === parseInt(id));
    if (index !== -1) {
      mockOrders.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Order not found', 'ERR_NOT_FOUND');
  },
  updateStatus: (id, status) => {
    const order = findById(mockOrders, id);
    if (order) {
      order.status = status;
      order.updatedAt = new Date().toISOString();
      return createMockResponse(order);
    }
    return createMockError('Order not found', 'ERR_NOT_FOUND');
  },
  getByCustomer: (customerId) => {
    const customerOrders = mockOrders.filter(o => o.customerId === parseInt(customerId));
    return createMockResponse(customerOrders);
  },
};

// Quotes API - Mock implementation
export const quotesAPI = {
  getAll: () => {
    // console.log('💰 Loading quotes from mock data');
    return createMockResponse(mockData.quotes || []);
  },
  getById: (id) => {
    const quote = findById(mockData.quotes || [], id);
    return quote ? createMockResponse(quote) : createMockError('Quote not found', 'ERR_NOT_FOUND');
  },
  create: (quote) => {
    if (!mockData.quotes) mockData.quotes = [];
    const newQuote = { ...quote, id: generateId(mockData.quotes), createdAt: new Date().toISOString() };
    mockData.quotes.push(newQuote);
    return createMockResponse(newQuote);
  },
  update: (id, quote) => {
    if (!mockData.quotes) return createMockError('Quote not found', 'ERR_NOT_FOUND');
    const index = mockData.quotes.findIndex(q => q.id === parseInt(id));
    if (index !== -1) {
      mockData.quotes[index] = { ...mockData.quotes[index], ...quote, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.quotes[index]);
    }
    return createMockError('Quote not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    if (!mockData.quotes) return createMockError('Quote not found', 'ERR_NOT_FOUND');
    const index = mockData.quotes.findIndex(q => q.id === parseInt(id));
    if (index !== -1) {
      mockData.quotes.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Quote not found', 'ERR_NOT_FOUND');
  },
  send: (id) => {
    const quote = findById(mockData.quotes || [], id);
    if (quote) {
      quote.status = 'sent';
      quote.sentAt = new Date().toISOString();
      return createMockResponse(quote);
    }
    return createMockError('Quote not found', 'ERR_NOT_FOUND');
  },
  convertToOrder: (id) => {
    const quote = findById(mockData.quotes || [], id);
    if (quote) {
      const newOrder = {
        ...quote,
        id: generateId(mockOrders),
        type: 'order',
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      mockOrders.push(newOrder);
      return createMockResponse(newOrder);
    }
    return createMockError('Quote not found', 'ERR_NOT_FOUND');
  },
};

// Purchase Orders API - Mock implementation
export const purchaseOrdersAPI = {
  getAll: () => {
    // console.log('📄 Loading purchase orders from mock data');
    return createMockResponse(mockData.purchaseOrders || []);
  },
  getById: (id) => {
    const po = findById(mockData.purchaseOrders || [], id);
    return po ? createMockResponse(po) : createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  create: (po) => {
    if (!mockData.purchaseOrders) mockData.purchaseOrders = [];
    const newPO = { ...po, id: generateId(mockData.purchaseOrders), createdAt: new Date().toISOString() };
    mockData.purchaseOrders.push(newPO);
    return createMockResponse(newPO);
  },
  update: (id, po) => {
    if (!mockData.purchaseOrders) return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
    const index = mockData.purchaseOrders.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      mockData.purchaseOrders[index] = { ...mockData.purchaseOrders[index], ...po, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.purchaseOrders[index]);
    }
    return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    if (!mockData.purchaseOrders) return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
    const index = mockData.purchaseOrders.findIndex(p => p.id === parseInt(id));
    if (index !== -1) {
      mockData.purchaseOrders.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  send: (id) => {
    const po = findById(mockData.purchaseOrders || [], id);
    if (po) {
      po.status = 'sent';
      po.sentAt = new Date().toISOString();
      return createMockResponse(po);
    }
    return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  receive: (id) => {
    const po = findById(mockData.purchaseOrders || [], id);
    if (po) {
      po.status = 'received';
      po.receivedAt = new Date().toISOString();
      return createMockResponse(po);
    }
    return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  updateStatus: (id, status) => {
    const po = findById(mockData.purchaseOrders || [], id);
    if (po) {
      po.status = status;
      po.updatedAt = new Date().toISOString();
      return createMockResponse(po);
    }
    return createMockError('Purchase order not found', 'ERR_NOT_FOUND');
  },
  getByVendor: (vendorId) => {
    const vendorPOs = (mockData.purchaseOrders || []).filter(po => po.vendorId === parseInt(vendorId));
    return createMockResponse(vendorPOs);
  },
};

// Receiving API - Mock implementation
export const receivingAPI = {
  getAll: () => {
    // console.log('📦 Loading receiving records from mock data');
    return createMockResponse(mockData.receivingRecords || []);
  },
  getById: (id) => {
    const record = findById(mockData.receivingRecords || [], id);
    return record ? createMockResponse(record) : createMockError('Receiving record not found', 'ERR_NOT_FOUND');
  },
  create: (record) => {
    if (!mockData.receivingRecords) mockData.receivingRecords = [];
    const newRecord = { ...record, id: generateId(mockData.receivingRecords), createdAt: new Date().toISOString() };
    mockData.receivingRecords.push(newRecord);
    return createMockResponse(newRecord);
  },
  update: (id, record) => {
    if (!mockData.receivingRecords) return createMockError('Receiving record not found', 'ERR_NOT_FOUND');
    const index = mockData.receivingRecords.findIndex(r => r.id === parseInt(id));
    if (index !== -1) {
      mockData.receivingRecords[index] = { ...mockData.receivingRecords[index], ...record, updatedAt: new Date().toISOString() };
      return createMockResponse(mockData.receivingRecords[index]);
    }
    return createMockError('Receiving record not found', 'ERR_NOT_FOUND');
  },
  delete: (id) => {
    if (!mockData.receivingRecords) return createMockError('Receiving record not found', 'ERR_NOT_FOUND');
    const index = mockData.receivingRecords.findIndex(r => r.id === parseInt(id));
    if (index !== -1) {
      mockData.receivingRecords.splice(index, 1);
      return createMockResponse({ success: true });
    }
    return createMockError('Receiving record not found', 'ERR_NOT_FOUND');
  },
  receiveItems: (data) => {
    const newRecord = { ...data, id: generateId(mockData.receivingRecords || []), createdAt: new Date().toISOString() };
    if (!mockData.receivingRecords) mockData.receivingRecords = [];
    mockData.receivingRecords.push(newRecord);
    return createMockResponse(newRecord);
  },
};

// Inventory API - Mock implementation
export const inventoryAPI = {
  getAll: () => {
    // console.log('📊 Loading inventory from mock data');
    return createMockResponse(mockData.products);
  },
  getByLocation: (locationId) => {
    const locationInventory = mockData.products.filter(p => p.locationId === parseInt(locationId));
    return createMockResponse(locationInventory);
  },
  adjustStock: (adjustments) => {
    adjustments.forEach(adj => {
      const product = findById(mockData.products, adj.productId);
      if (product) {
        product.stock += adj.adjustment;
      }
    });
    return createMockResponse({ success: true, adjustments });
  },
  transfer: (transfer) => {
    const product = findById(mockData.products, transfer.productId);
    if (product) {
      return createMockResponse({ success: true, transfer });
    }
    return createMockError('Product not found', 'ERR_NOT_FOUND');
  },
  getLowStock: () => {
    const lowStockProducts = mockData.products.filter(p => p.stock <= p.reorderPoint);
    return createMockResponse(lowStockProducts);
  },
};

// Reports API - Mock implementation
export const reportsAPI = {
  getSalesReport: (params) => {
    // console.log('📈 Loading sales report from mock data');
    return createMockResponse(mockReportsData.sales || []);
  },
  getInventoryReport: (params) => {
    // console.log('📉 Loading inventory report from mock data');
    return createMockResponse(mockReportsData.inventory || []);
  },
  getPurchaseReport: (params) => {
    // console.log('📊 Loading purchase report from mock data');
    return createMockResponse(mockReportsData.purchase || []);
  },
  getCustomerReport: (params) => {
    // console.log('👥 Loading customer report from mock data');
    return createMockResponse(mockReportsData.customers || []);
  },
  getDashboardData: () => {
    // console.log('📊 Loading dashboard data from mock data');
    return createMockResponse(mockDashboardData);
  },
  exportReport: (type, params) => {
    console.log('📥 Mock export report:', type);
    return createMockResponse({ message: 'Report exported successfully', type });
  },
};

// Barcode API - Mock implementation
export const barcodeAPI = {
  generate: (productId) => {
    const product = findById(mockData.products, productId);
    if (product) {
      const barcode = `${Date.now()}${productId}`;
      return createMockResponse({ barcode, productId });
    }
    return createMockError('Product not found', 'ERR_NOT_FOUND');
  },
  scan: (barcode) => {
    const product = mockData.products.find(p => p.barcode === barcode);
    if (product) {
      return createMockResponse(product);
    }
    return createMockError('Product not found for barcode', 'ERR_NOT_FOUND');
  },
  print: (barcodes) => {
    console.log('🖨️ Mock print barcodes:', barcodes);
    return createMockResponse({ message: 'Barcodes printed successfully', count: barcodes.length });
  },
};

// Export a mock API object for compatibility
const mockApi = {
  get: (url) => {
    console.log('🚀 Mock API GET:', url);
    return createMockResponse({ message: 'Mock API response' });
  },
  post: (url, data) => {
    console.log('🚀 Mock API POST:', url, data);
    return createMockResponse({ message: 'Mock API response', data });
  },
  put: (url, data) => {
    console.log('🚀 Mock API PUT:', url, data);
    return createMockResponse({ message: 'Mock API response', data });
  },
  delete: (url) => {
    console.log('🚀 Mock API DELETE:', url);
    return createMockResponse({ message: 'Mock API response' });
  },
  patch: (url, data) => {
    console.log('🚀 Mock API PATCH:', url, data);
    return createMockResponse({ message: 'Mock API response', data });
  },
};

export default mockApi;
